import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CacheBucket, HttpCacheManager, withCache } from '@ngneat/cashew';
import { environment } from '@y42/environment';
import { setStorageIdHttpContext } from '@y42/http';
import { GitHttpHeader, ORG_API_URL_PARAM } from '@y42/model';
import { map, Observable, tap } from 'rxjs';
import {
  AclCheckResourcePermissionsHttpModel,
  AclGrantHttpModel,
  AclPermission,
  AclResource,
  AclResourceType,
  AclRole,
  AclRoleAssignment,
  AclRolesState,
  AclRoleTooltipsState,
  AclOrgSpaceSettings,
  AclSubject,
} from '../models';

@Injectable({ providedIn: 'root' })
export class AclHttpService {
  constructor(private readonly http: HttpClient, private readonly httpCacheManager: HttpCacheManager) {}

  private readonly url = environment.url.acl_api;
  private readonly cacheBucket = new CacheBucket();

  getRoles(): Observable<AclRolesState> {
    return this.http.get<AclRolesState>(`${this.url}/list_resource_roles`);
  }

  getRoleTooltips(): Observable<AclRoleTooltipsState> {
    return this.http.get<AclRoleTooltipsState>(`${this.url}/tooltips`);
  }

  getSpaceSettings({ spaceId }: { spaceId: string }): Observable<AclOrgSpaceSettings> {
    return this.http.get<AclOrgSpaceSettings>(`${this.getOrgBaseUrl()}/space/${spaceId}/settings`);
  }

  setSpaceSettings({ spaceId, settings }: { spaceId: string; settings: AclOrgSpaceSettings }): Observable<boolean> {
    return this.http.post<unknown>(`${this.getOrgBaseUrl()}/space/${spaceId}/settings`, settings).pipe(map(() => true));
  }

  checkPermissions({
    resourceType,
    resourceId,
    permissions,
    spaceId,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    permissions: AclPermission[];
    spaceId?: string;
  }): Observable<AclCheckResourcePermissionsHttpModel> {
    let params = new HttpParams({
      fromObject: {
        resource_id: resourceId,
      },
    });

    permissions?.forEach((permission) => {
      params = params.append('permission', permission);
    });

    return this.http
      .get<AclCheckResourcePermissionsHttpModel>(
        `${this.getOrgBaseUrl(resourceType)}/permissions/${resourceType}/check`,
        {
          params,
          context: withCache({
            bucket: this.cacheBucket,
          }),
          headers: spaceId
            ? new HttpHeaders({
                [GitHttpHeader.Id]: spaceId,
              })
            : undefined,
        },
      )
      .pipe(
        map((response) => ({
          ...response,
          permissions: response.permissions.map((permission) => ({
            ...permission,
            // Sometimes it returns as empty string or null, normalize to undefined
            detail: permission.detail || undefined,
          })),
        })),
        tap(() => {
          this.httpCacheManager.delete(this.cacheBucket);
        }),
      );
  }

  /**
   * The permissions always belong to the current user and the roles belong to the subject,
   * therefore the roles returned from an enumerate call depend on the subject that was sent.
   */
  enumerateResources({
    resourceType,
    permissions,
    subject,
    spaceId,
  }: {
    resourceType: AclResourceType;
    subject?: Partial<AclSubject>;
    permissions: AclPermission[];
    spaceId?: string;
  }): Observable<AclResource[]> {
    let params = new HttpParams();

    permissions?.forEach((permission) => {
      params = this.appendOptionalParam(params, 'permission', permission);
    });
    params = this.appendOptionalParam(params, 'return_subject_roles', !!subject);
    params = this.appendOptionalParam(params, 'return_roles_for_subject_type', subject?.subject_type);
    params = this.appendOptionalParam(params, 'return_roles_for_subject_id', subject?.subject_id);

    return this.http
      .get<{ allowed_resources: AclResource[] }>(`${this.getOrgBaseUrl(resourceType)}/enumerate/${resourceType}`, {
        params,
        headers: spaceId
          ? new HttpHeaders({
              [GitHttpHeader.Id]: spaceId,
            })
          : undefined,
      })
      .pipe(map((response) => response.allowed_resources));
  }

  getRoleAssignments({
    resourceType,
    resourceId,
    spaceId,
    subject,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    spaceId?: string;
    subject?: AclSubject;
  }): Observable<AclRoleAssignment[]> {
    let params = new HttpParams({
      fromObject: {
        resource_id: resourceId,
      },
    });

    params = this.appendOptionalParam(params, 'only_subject_type', subject?.subject_type);
    params = this.appendOptionalParam(params, 'only_subject_id', subject?.subject_id);

    return this.http
      .get<{ assignments: AclRoleAssignment[] }>(`${this.getOrgBaseUrl(resourceType)}/user_roles/${resourceType}`, {
        params,
        context: spaceId ? setStorageIdHttpContext(spaceId) : undefined,
      })
      .pipe(map((response) => response.assignments));
  }

  setRole({
    resourceType,
    resourceId,
    spaceId,
    subject,
    role,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    spaceId?: string;
    subject: AclSubject;
    role: AclRole;
  }): Observable<boolean> {
    return this.setGrant({
      resourceType,
      resourceId,
      spaceId,
      subject,
      role,
      revoke: false,
    });
  }

  revokeRole({
    resourceType,
    resourceId,
    spaceId,
    subject,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    spaceId?: string;
    subject: AclSubject;
  }): Observable<boolean> {
    return this.setGrant({
      resourceType,
      resourceId,
      spaceId,
      subject,
      revoke: true,
    });
  }

  private setGrant({
    resourceType,
    resourceId,
    subject,
    spaceId,
    role,
    revoke,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    subject: AclSubject;
    role?: AclRole;
    spaceId?: string;
    revoke: boolean;
  }): Observable<boolean> {
    const params = new HttpParams({
      fromObject: {
        resource_id: resourceId,
      },
    });

    const grant: AclGrantHttpModel = {
      receiver_subject_type: subject.subject_type,
      receiver_subject_id: subject.subject_id,
      role,
      revoke,
    };

    return this.http
      .post<unknown>(`${this.getOrgBaseUrl(resourceType)}/grants/${resourceType}`, grant, {
        params,
        headers: spaceId
          ? new HttpHeaders({
              [GitHttpHeader.Id]: spaceId,
            })
          : undefined,
      })
      .pipe(map(() => true));
  }

  private getOrgBaseUrl(resourceType?: AclResourceType) {
    return resourceType === AclResourceType.Org || resourceType === AclResourceType.User
      ? this.url
      : `${this.url}/company/${ORG_API_URL_PARAM}`;
  }

  private appendOptionalParam(params: HttpParams, paramName: string, value?: unknown) {
    return value ? params.append(paramName, value as string) : params;
  }
}
